import React, { useEffect, useRef, useState } from "react";
import {
  Box,
  CircularProgress,
  FormControl,
  InputLabel,
  MenuItem,
  Select,
  Typography,
} from "@mui/material";
import SelectArrow from "../../styles/icons/SelectArrow";
import { journeyActionConditionConfig } from "../../utils/workflow/journeyActionConditions";

const COLOR_HEADER = "#1E1E1E";
const COLOR_ACCENT = "#0DA3E2";

const SELECT_MENU_PROPS = {
  PaperProps: {
    sx: {
      mt: 0.5,
      maxHeight: 320,
      overflowY: "auto",
      bgcolor: COLOR_HEADER,
      backgroundImage: "none",
      color: "#FFFFFF",
      border: `1px solid ${COLOR_ACCENT}`,
      borderRadius: "3px",
      "& .MuiMenuItem-root": {
        color: "#FFFFFF",
        minHeight: 42,
        fontSize: "14px",
        "&.Mui-selected, &.Mui-selected:hover": {
          backgroundColor: "rgba(13,163,226,0.22)",
        },
        "&:hover": {
          backgroundColor: "rgba(13,163,226,0.12)",
        },
      },
    },
  },
};

const segmentSelectSx = {
  minWidth: 0,
  "& .MuiOutlinedInput-notchedOutline": { border: "none" },
  "& .MuiSelect-select": {
    py: 0.25,
    px: 0.75,
    fontSize: "0.8rem",
    overflow: "hidden",
    textOverflow: "ellipsis",
    whiteSpace: "nowrap",
    minWidth: 0,
  },
};

const formatActionLabel = (action) => {
  const parts = [action.templateName];
  if (action.automationName) {
    parts.push(`(${action.automationName})`);
  }
  if (action.executionCount > 0) {
    parts.push(`[${action.executionCount}]`);
  }
  return parts.join(" ");
};

export default function JourneyActionConditionFields({
  condition,
  onUpdate,
  brandId,
  onLoadActionInstances,
  segmentSx: segmentSxProp,
  cols,
}) {
  const [actionInstances, setActionInstances] = useState([]);
  const [loadingActions, setLoadingActions] = useState(false);
  const [loadError, setLoadError] = useState(null);
  const [usingTemplateFallback, setUsingTemplateFallback] = useState(false);

  const segmentSx = segmentSxProp || {};
  const COLS = cols || {
    field: { flex: "0 0 112px", maxWidth: 125 },
    operator: { flex: "0 0 108px", maxWidth: 120 },
    value: { flex: "1 1 120px", minWidth: 120 },
  };

  const loadActionInstancesRef = useRef(onLoadActionInstances);
  loadActionInstancesRef.current = onLoadActionInstances;

  useEffect(() => {
    if (!brandId || !condition.channelType) {
      setActionInstances([]);
      setUsingTemplateFallback(false);
      setLoadError(null);
      return;
    }

    let cancelled = false;

    const load = async () => {
      setLoadingActions(true);
      setLoadError(null);
      try {
        const loader = loadActionInstancesRef.current;
        if (!loader) {
          setActionInstances([]);
          return;
        }

        const result = await loader(brandId, condition.channelType);
        if (cancelled) return;
        setActionInstances(result?.items || []);
        setUsingTemplateFallback(Boolean(result?.usedTemplateFallback));
        if (!result?.items?.length) {
          setLoadError("No actions available for this channel");
        }
      } catch (error) {
        if (cancelled) return;
        console.warn("Failed to load journey action instances:", error);
        setActionInstances([]);
        setLoadError("Could not load actions");
        setUsingTemplateFallback(false);
      } finally {
        if (!cancelled) setLoadingActions(false);
      }
    };

    load();

    return () => {
      cancelled = true;
    };
  }, [brandId, condition.channelType]);

  const selectedTemplateId =
    condition.actionRef?.templateId != null
      ? String(condition.actionRef.templateId)
      : condition.value
      ? String(condition.value)
      : "";

  return (
    <>
      <Box sx={{ ...segmentSx, ...COLS.field }}>
        <FormControl size="small" fullWidth>
          <InputLabel sx={{ fontSize: "0.72rem" }}>Execution Status</InputLabel>
          <Select
            size="small"
            IconComponent={SelectArrow}
            value={condition.executionState || ""}
            MenuProps={SELECT_MENU_PROPS}
            onChange={(e) =>
              onUpdate({
                ...condition,
                executionState: e.target.value,
                field: e.target.value,
              })
            }
            sx={segmentSelectSx}
          >
            {journeyActionConditionConfig.executionStates.map((option) => (
              <MenuItem key={option.value} value={option.value}>
                {option.label}
              </MenuItem>
            ))}
          </Select>
        </FormControl>
      </Box>

      <Box sx={{ ...segmentSx, ...COLS.operator }}>
        <FormControl size="small" fullWidth>
          <InputLabel sx={{ fontSize: "0.72rem" }}>Delivery Channel</InputLabel>
          <Select
            size="small"
            IconComponent={SelectArrow}
            value={condition.channelType || ""}
            MenuProps={SELECT_MENU_PROPS}
            onChange={(e) =>
              onUpdate({
                ...condition,
                channelType: e.target.value,
                operator: e.target.value,
                actionRef: null,
                actionLabel: "",
                value: "",
              })
            }
            sx={segmentSelectSx}
          >
            {journeyActionConditionConfig.channelTypes.map((option) => (
              <MenuItem key={option.value} value={option.value}>
                {option.label}
              </MenuItem>
            ))}
          </Select>
        </FormControl>
      </Box>

      <Box sx={{ ...segmentSx, ...COLS.value }}>
        {condition.executionState && condition.channelType ? (
          <FormControl size="small" fullWidth>
            <InputLabel sx={{ fontSize: "0.72rem" }}>Action</InputLabel>
            <Select
              size="small"
              IconComponent={SelectArrow}
              value={selectedTemplateId}
              MenuProps={SELECT_MENU_PROPS}
              disabled={loadingActions}
              onChange={(e) => {
                const templateId = e.target.value;
                const selected = actionInstances.find(
                  (item) => String(item.templateId) === String(templateId)
                );
                onUpdate({
                  ...condition,
                  value: String(templateId),
                  actionRef: selected
                    ? {
                        templateId: selected.templateId,
                        templateName: selected.templateName,
                        automationId: selected.automationId,
                        automationName: selected.automationName,
                        nodeId: selected.nodeId,
                      }
                    : { templateId, templateName: "" },
                  actionLabel: selected?.templateName || "",
                });
              }}
              sx={segmentSelectSx}
            >
              {loadingActions && (
                <MenuItem disabled value="">
                  <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                    <CircularProgress size={14} />
                    Loading actions...
                  </Box>
                </MenuItem>
              )}
              {!loadingActions && actionInstances.length === 0 && (
                <MenuItem disabled value="">
                  {loadError || "No actions found for this channel"}
                </MenuItem>
              )}
              {actionInstances.map((action) => (
                <MenuItem
                  key={`${action.channelType}-${action.templateId}-${action.automationId || "tpl"}`}
                  value={String(action.templateId)}
                >
                  {formatActionLabel(action)}
                </MenuItem>
              ))}
            </Select>
            {usingTemplateFallback && (
              <Typography
                variant="caption"
                sx={{ display: "block", mt: 0.5, color: "warning.main", px: 0.5 }}
              >
                Showing templates until execution log API is available
              </Typography>
            )}
          </FormControl>
        ) : (
          <FormControl size="small" disabled fullWidth>
            <InputLabel sx={{ color: "text.disabled" }}>Action</InputLabel>
            <Select size="small" IconComponent={SelectArrow} value="" sx={segmentSelectSx}>
              <MenuItem value="">Select channel first</MenuItem>
            </Select>
          </FormControl>
        )}
      </Box>
    </>
  );
}
